<script lang="ts">
export interface ButtonProps {
  label?: string;
  iconLeft?: string;
  iconRight?: string;
  kind?: "primary" | "ghost";
  squared?: boolean;
}
</script>

<script setup lang="ts">
const {
  label = null,
  iconLeft = null,
  iconRight = null,
  kind = "primary",
  squared = false,
} = defineProps<ButtonProps>();
</script>

<template>
  <button class="skore-button" :class="[kind, { squared }]">
    <i v-if="iconLeft !== null" :class="[iconLeft]" class="icon left" />
    <slot name="labelPrefix"></slot>
    <span v-if="label" class="label">{{ label }}</span>
    <i v-if="iconRight !== null" :class="[iconRight]" class="icon right" />
  </button>
</template>

<style>
/* This style block is not scoped to allow reuse of button skin in links. */
.skore-button.primary {
  --color: var(--content-primary-inverse);
  --color-disabled: var(--content-disabled);
  --background-color: var(--background-brand);
  --background-color-hover: var(--background-brand-hover);
  --background-color-disabled: var(--background-disabled);
  --shadow-color-active: rgb(196 202 255 / 51%);
}

.skore-button.ghost {
  --color: var(--content-secondary);
  --color-disabled: var(--content-disabled);
  --background-color: var(--background-primary);
  --background-color-hover: var(--background-hover);
  --background-color-disabled: var(--background-disabled);
  --shadow-color-active: rgb(231 231 231 / 25%);

  border: solid var(--border-tertiary) var(--stroke-width-md);
}

.skore-button {
  --height: var(--numbers-40);

  display: inline-flex;
  min-width: var(--height);
  height: var(--height);
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: var(--numbers-16);
  border: none;
  border-radius: var(--numbers-8);
  background-color: var(--background-color);
  color: var(--color);
  cursor: pointer;
  font-size: 1rem;
  font-weight: var(--fonts-font-weight-regular);
  letter-spacing: 0.017em;
  text-decoration: none;
  transition:
    box-shadow var(--animation-duration) var(--animation-easing),
    color var(--animation-duration) var(--animation-easing),
    background-color var(--animation-duration) var(--animation-easing);

  &:hover {
    background-color: var(--background-color-hover);
  }

  &:disabled {
    background-color: var(--background-color-disabled);
    color: var(--color-disabled);
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: none;
  }

  &:visited {
    color: var(--color);
  }

  &:active:not(:disabled) {
    box-shadow:
      0 0 0 0.25rem rgb(6 43 205 / 30%),
      0 0 0 0.125rem rgb(255 255 255 / 30%);
  }

  .icon.left:has(+ .label) {
    padding-right: var(--numbers-8);
  }

  .label + .icon.right {
    padding-left: var(--numbers-8);
  }

  .label {
    display: inline-block;
    overflow: hidden;
    max-width: 100%;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &.squared {
    width: var(--height);
    min-width: var(--height);
    max-width: var(--height);
    padding: 0;
  }
}
</style>
